import { ThemeOptions, createTheme } from "@mui/material";
import { deepmerge } from "@mui/utils";

import { ENERMATCH_COLORS } from "./enermatch";

type NeutralBGTypes =
  | "pageGradient"
  | "sectionGradient"
  | "primary"
  | "secondary";

type ExtendedChartsCategories =
  | "primary"
  | "secondary"
  | "primaryDark"
  | "secondaryDark"
  | "border";

declare module "@mui/material/styles" {
  interface Theme {
    shadow: string;
  }

  interface Palette {
    backgrounds: Partial<Record<NeutralBGTypes, string>>;
    charts: Record<ExtendedChartsCategories, string>;
  }

  interface ThemeOptions {
    shadow?: string;
  }

  interface PaletteOptions {
    backgrounds?: Partial<Record<NeutralBGTypes, string>>;
    charts: Record<ExtendedChartsCategories, string>;
  }
}

export const createWhitelabelTheme = (brandColors: ThemeOptions | {}) => {
  // we are always building on top of our theme, not Paperbase
  const baseConfig = deepmerge(ENERMATCH_COLORS, brandColors);

  let theme = createTheme(baseConfig);

  // Typography and components get configured based on the brand's theme
  theme = createTheme(theme, {
    typography: {
      h1: {
        color: theme.palette.text.primary,
        fontSize: "1.5rem", //24px
        fontWeight: "bold",
        letterSpacing: "0.17px",
        lineHeight: "1.75rem",
        marginBottom: theme.spacing(2),
      },
      h2: {
        color: theme.palette.text.primary,
        fontSize: "1.25rem", //20px
        fontWeight: "bold",
        lineHeight: "1.375rem",
      },
      h3: {
        color: theme.palette.text.primary,
        fontSize: "0.9375rem", //15px
        fontWeight: 600,
        lineHeight: "normal",
      },
      subtitle1: {
        color: theme.palette.primary.dark,
        fontSize: "1rem", //16px
        fontWeight: 500,
        lineHeight: "150%",
      },
      body1: {
        color: theme.palette.text.primary,
        fontSize: "0.875rem", //14px
        lineHeight: "143%",
      },
      body2: {
        color: theme.palette.text.primary,
        fontSize: "0.875rem", //14px
        fontWeight: 600,
        lineHeight: "143%",
      },
      caption: {
        color: theme.palette.text.primary,
        fontSize: "0.75rem", //12px
        lineHeight: "150%",
      },
    },
    components: {
      MuiAccordion: {
        styleOverrides: {
          root: {
            "&, &:last-of-type, &:first-of-type": {
              borderBottomLeftRadius: "18px",
              borderBottomRightRadius: "18px",
              borderTopLeftRadius: "18px",
              borderTopRightRadius: "18px",
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: theme.palette.backgrounds.sectionGradient,
            boxShadow: theme.shadow,
            height: theme.spacing(8),
            position: "sticky",
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            // Ensures Toolbar will stretch to parent's size instead of auto sizing based on the height of the content
            height: "100%",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            "&.MuiOutlinedInput-root": { borderRadius: "18px" },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            boxSizing: "border-box",
            borderRadius: "0 15px 15px 0",
            background: theme.palette.backgrounds.pageGradient,
            padding: theme.spacing(2),
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            minHeight: "48px",
            borderRadius: "16px",
            fontSize: "15px", //h3
            fontWeight: 600, // h3
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            "&.MuiTooltip-tooltip": {
              "&.MuiTooltip-tooltipPlacementBottom": {
                marginTop: "2px",
              },
              "&.MuiTooltip-tooltipPlacementTop": {
                marginBottom: "2px",
              },
              "&.MuiTooltip-tooltipPlacementLeft": {
                marginRight: "2px",
              },
              "&.MuiTooltip-tooltipPlacementRight": {
                marginLeft: "2px",
              },
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          flexContainer: {
            borderBottom: `2px solid ${theme.palette.secondary.light}`,
          },
          root: {
            marginBottom: theme.spacing(1),
            minHeight: 0,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: theme.palette.secondary.main,
            justifyContent: "center",
            lineHeight: theme.typography.body1.fontSize,
            minHeight: 0,
            textTransform: "capitalize",
            padding: theme.spacing(1, 2),
            "&.Mui-selected": {
              fontWeight: 700,
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: "18px",
          },
          outlinedSuccess: {
            "&.MuiAlert-outlinedSuccess": {
              backgroundColor: theme.palette.success.light,
              borderColor: theme.palette.success.main,
            },
            "&.MuiAlert-outlinedSuccess .MuiAlert-icon": {
              color: theme.palette.success.main,
            },
          },
          outlinedWarning: {
            "&.MuiAlert-outlinedWarning": {
              backgroundColor: theme.palette.warning.light,
              borderColor: theme.palette.warning.main,
            },
            "&.MuiAlert-outlinedWarning .MuiAlert-icon": {
              color: theme.palette.warning.main,
            },
          },
          outlinedError: {
            "&.MuiAlert-outlinedError": {
              backgroundColor: theme.palette.error.light,
              borderColor: theme.palette.error.main,
            },
            "&.MuiAlert-outlinedError .MuiAlert-icon": {
              color: theme.palette.error.main,
            },
          },
          standardInfo: {
            "&.MuiAlert-standardInfo": {
              backgroundColor: "white",
              color: theme.palette.text.primary,
              "& >.MuiAlert-icon": {
                color: theme.palette.primary.main,
              },
            },
          },
          standardError: {
            "&.MuiAlert-standardError": {
              backgroundColor: theme.palette.error.light,
              color: theme.palette.text.primary,
              "& >.MuiAlert-icon, & >.MuiAlert-action > button": {
                color: theme.palette.error.main,
              },
            },
          },
        },
      },
    },
  });
  return theme;
};
